import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect, useHistory, useLocation, useParams } from 'react-router-dom';
import AppPaths from '../../AppPaths';
import PageSeo from '../../components/PageSeo';
import { MARTINGALE_BAND_PRODUCT_ID } from '../../lib/product';
import { cartActions as storeCartActions } from '../../reducers/storeCart';
import * as types from '../../types';
import { series2CollarId, series3CollarId, series3PlusCollarId } from '../../types';
import EndlinksDetails from '../Endlinks';
import NotFound from '../NotFound';
import BandDetails from './BandDetails';
import ChargingBaseDetails from './ChargingBaseDetails';
import MartingaleBandDetails from './MartingaleBandDetails';
import MiniKitDetails from './MiniKitDetails';
import NanoDetails from './NanoDetails';
import Series2CollarDetails from './Series2CollarDetails';
import Series3CollarDetails from './Series3CollarDetails';
import { getFiGrowthBook, useIsGrowthBookEnabled } from '../../lib/growthbook';
import { useLocale } from '../../contexts/LocaleContext';
import { getLocalizedPricing } from '../../lib/util';
import { isInAppShop } from '../../lib/inAppShop';

/**
 * Resolves the route's `:id` to a product. Pure — a param read, a selector and one memo — so the
 * title wrapper and the category switch below can each call it and are guaranteed to agree on the
 * same product.
 */
function useRoutedProduct() {
  const { id: productId } = useParams<{ id: string }>();
  const products = useSelector((state: types.AppState) => state.config.products);

  return useMemo(() => {
    return products.find((p) => p.id === productId);
  }, [productId, products]);
}

/**
 * True on `/products/smart-collar-v3` while the Series 3+ experience is on.
 *
 * That route used to render the Series 3+ product *in place* by rewriting the id, which left two
 * URLs serving one product — and now that every route self-canonicalises, the stale slug actively
 * asserts itself as the representative page. It is also live traffic: ~10.7k journeys a quarter
 * enter on that slug. Redirecting instead of rewriting leaves one URL per product.
 *
 * Still gated on the same flag: turning the experience off restores the real Series 3 page rather
 * than stranding the route.
 */
function useShouldCanonicalizeToSeries3Plus() {
  const { id: productId } = useParams<{ id: string }>();

  const seriesThreePlusExperience = useIsGrowthBookEnabled()
    ? getFiGrowthBook().getFeatureValue<boolean>('series-three-plus-experience', false)
    : false;

  return seriesThreePlusExperience && productId === series3CollarId;
}

/**
 * Gives each PDP its own <title>. Without this the whole storefront shares the single tag set in
 * Series3App, which is why Search Console showed every product URL indexed under one title.
 */
export default function ProductDetails() {
  const { t } = useTranslation();
  const location = useLocation();
  const product = useRoutedProduct();
  const shouldCanonicalizeToSeries3Plus = useShouldCanonicalizeToSeries3Plus();

  // Ahead of PageSeo deliberately: the legacy slug should never publish a Series 3 title, not even
  // for the frame before the hop. Preserve query and hash so campaign params (utm_*, ?type=) survive.
  if (shouldCanonicalizeToSeries3Plus) {
    return (
      <Redirect
        to={{ pathname: AppPaths.Products(series3PlusCollarId), search: location.search, hash: location.hash }}
      />
    );
  }

  return (
    <>
      {product ? (
        <PageSeo
          title={t('seo_product_title', { productName: product.name })}
          description={t('seo_product_description', { productName: product.name })}
        />
      ) : null}
      <ProductDetailsContent />
    </>
  );
}

function ProductDetailsContent() {
  const dispatch = useDispatch();
  const history = useHistory();
  const { currency } = useLocale();
  const product = useRoutedProduct();

  if (!product) {
    return <NotFound />;
  }

  if (product.category === types.ProductCategory.CHARGING_BASE) {
    const priceInCents = getLocalizedPricing(product.pricingInCents, currency);
    return (
      <ChargingBaseDetails
        productId={product.id}
        productName={product.name}
        priceInCents={priceInCents}
        sku={product.sku}
      />
    );
  }

  if (product.category === types.ProductCategory.MINI_ATTACHMENT) {
    const priceInCents = getLocalizedPricing(product.pricingInCents, currency);
    return <MiniKitDetails productId={product.id} priceInCents={priceInCents} sku={product.sku} />;
  }

  if (product.category === types.ProductCategory.MICROCHIP) {
    const priceInCents = getLocalizedPricing(product.pricingInCents, currency);
    return <NanoDetails productName={product.name} priceInCents={priceInCents} sku={product.sku} />;
  }

  if (product.category === types.ProductCategory.ENDLINK) {
    return <EndlinksDetails />;
  }

  if (
    product.category === types.ProductCategory.COLLAR_KIT &&
    (product.id === series3CollarId || product.id === series3PlusCollarId)
  ) {
    const priceInCents = getLocalizedPricing(product.pricingInCents, currency);
    return (
      <>
        <Series3CollarDetails
          addBundleCartItem={(variantSku, subscriptionSku, addonSkus) => {
            dispatch(
              storeCartActions.addBundleCartItem({
                sku: variantSku,
                subscriptionSku: subscriptionSku,
                addonSkus: addonSkus,
                currency,
              }),
            );

            if (isInAppShop()) {
              dispatch(storeCartActions.setShowInAppCheckout(true));
            } else {
              history.push(AppPaths.Bag);
            }
          }}
          priceInCents={priceInCents}
          productName={product.name}
          variants={product.variants}
          collarId={product.id}
        />
      </>
    );
  }

  if (product.category === types.ProductCategory.COLLAR_KIT && product.id === series2CollarId) {
    const priceInCents = getLocalizedPricing(product.pricingInCents, currency);
    return <Series2CollarDetails priceInCents={priceInCents} productName={product.name} variants={product.variants} />;
  }

  if (product.category === types.ProductCategory.BAND && product.id === MARTINGALE_BAND_PRODUCT_ID) {
    const priceInCents = getLocalizedPricing(product.pricingInCents, currency);
    return <MartingaleBandDetails priceInCents={priceInCents} productName={product.name} variants={product.variants} />;
  }

  if (product.category === types.ProductCategory.BAND) {
    const priceInCents = getLocalizedPricing(product.pricingInCents, currency);
    return (
      <BandDetails
        bandSeries={product.bandSeries}
        priceInCents={priceInCents}
        productId={product.id}
        productName={product.name}
        variants={product.variants}
      />
    );
  }

  return <NotFound />;
}
